// @flow
import React from 'react';
import Modal from 'react-modal';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SimpleBar from 'simplebar-react';
import { BedsFilter } from 'components/molecules/bedsFilter';
import { LocationFilter } from 'components/molecules/locationFilter';
import { PropertyTypeFilter } from 'components/molecules/propertyTypeFilter';
import { useViewport } from 'components/templates/viewportProvider';
import { AreaFilter } from 'components/molecules/areaFilter';
import { RangeFilter } from 'components/molecules/rangeFilter';
import { RentalSelector } from 'modules/rentals/selector';
import { RentalActions } from 'modules/rentals/actions';
import chevronRightIcon from 'assets/images/rightb.png';
import type { LocationCityOption, PropertyTypeOption } from 'types/filter';
// eslint-disable-next-line
import styles from "../projectFiltersModal/styles.scss";

type Props = {
  isOpen: boolean,
  onClosePress: () => void,
  filters: any,
  activeFilters: any,
  toggleRentalFilter: (type: string, value: number) => void,
  clearRentalFilters: () => void,
};

const RentalFiltersModal = (props: Props) => {
  const { isMobile } = useViewport();
  const {
    isOpen,
    onClosePress,
    filters,
    activeFilters,
    toggleRentalFilter,
    clearRentalFilters,
  } = props;
  if (!filters) {
    return null;
  }

  const onClose = () => {
    onClosePress();
  };

  const toggleLocation = (option: LocationCityOption) => {
    toggleRentalFilter('location', option.id);
  };

  const togglePropertyType = (option: PropertyTypeOption) => {
    toggleRentalFilter('propertyType', option.id);
  };

  const changeBeds = (value: Array<number>) => {
    toggleRentalFilter('beds', value[0]);
  };

  const changeArea = (values: Array<number>) => {
    toggleRentalFilter('area', values);
  };

  const setRange = (value: Array<number>) => {
    toggleRentalFilter('range', value);
  };

  const clearAllFilters = e => {
    e.preventDefault();
    clearRentalFilters();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClosePress}
      overlayClassName={'modal-overlay normal-scroll'}
      className={`project-filters-overlay-content ${isMobile ? 'mobile' : ''}`}
    >
      <div className={'project-filters-modal'}>
        <a className={'collapse-icon-container'} onClick={onClose}>
          <img src={chevronRightIcon} alt={'icon'} />
        </a>
        {!isMobile && <span className={'modal-title'}>Filters</span>}
        <a className={'modal-button'} onClick={clearAllFilters} href={'#'}>
          Clear All
        </a>
        <SimpleBar autoHide={true} style={{ height: '100%' }}>
          <div className={'project-filters-content'}>
            <div className={'filters-header'}>
              <h3>Looking for something specific?</h3>
              {!isMobile && (
                <p>Filter by location, project status, and property type.</p>
              )}
            </div>
            <div className={'filter-set'}>
              <LocationFilter
                activeFilters={activeFilters.location}
                toggleFilter={city => toggleLocation(city)}
                options={filters.location}
              />
              <PropertyTypeFilter
                activeFilters={activeFilters.propertyType}
                toggleFilter={type => togglePropertyType(type)}
                options={filters.propertyType}
              />
              <BedsFilter
                min={filters.beds.min}
                max={filters.beds.max}
                values={activeFilters.beds ? [activeFilters.beds] : null}
                onChange={value => changeBeds(value)}
              />
              <AreaFilter
                min={filters.area.min}
                max={filters.area.max}
                values={
                  activeFilters.area || [filters.area.min, filters.area.max]
                }
                onChange={values => changeArea(values)}
              />
              <RangeFilter
                min={filters.range.min}
                max={filters.range.max}
                onChange={values => setRange(values)}
                values={activeFilters.range ? [activeFilters.range] : null}
              />
            </div>
          </div>
        </SimpleBar>
      </div>
    </Modal>
  );
};

const mapStateToProps = state => {
  return {
    filters: RentalSelector.getRentalFilters(state),
    activeFilters: RentalSelector.getActiveFilters(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { toggleRentalFilter, clearRentalFilters } = RentalActions;
  return bindActionCreators(
    {
      toggleRentalFilter,
      clearRentalFilters,
    },
    dispatch
  );
};

const connectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(RentalFiltersModal);

export { connectedComponent as RentalFiltersModal };
